import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication
 * Redirects to login if user is not authenticated
 * Can also check for specific roles
 */
export const ProtectedRoute = ({ children, allowedRoles = null, loading: isPageLoading = false }) => {
  const { isAuthenticated, user, loading: authLoading, logout } = useAuth();
  const location = useLocation();

  // Keep the sidebar and direct URL access consistent. A hidden menu must not
  // remain reachable simply by typing its URL in the browser.
  const routeRoles = (pathname) => {
    if (pathname === '/cms' || pathname.startsWith('/cms/')) return ['Admin', 'SuperAdmin'];
    if (pathname === '/rbac' || pathname.startsWith('/rbac/')) return ['Admin', 'SuperAdmin'];
    if (pathname.startsWith('/products') || pathname.startsWith('/pricing')) return ['Admin', 'SuperAdmin', 'Manager', 'Staff'];
    if (pathname.startsWith('/orders') || pathname.startsWith('/store-operations')) return ['Admin', 'SuperAdmin', 'Manager', 'Staff', 'Customer'];
    if (pathname.startsWith('/supplier-management') || pathname.startsWith('/purchase-management') || pathname.startsWith('/finance-management') || pathname.startsWith('/store-setup') || pathname.startsWith('/franchisee')) return ['Admin', 'SuperAdmin', 'Manager'];
    if (pathname.startsWith('/employees') || pathname.startsWith('/departments') || pathname.startsWith('/onboarding')) return ['Admin', 'SuperAdmin', 'Manager', 'HR Manager'];
    if (pathname.startsWith('/hrms')) return ['Admin', 'SuperAdmin', 'Manager', 'HR Manager'];
    if (pathname.startsWith('/attendance') || pathname.startsWith('/leaves') || pathname.startsWith('/shifts') || pathname.startsWith('/holidays')) return ['Admin', 'SuperAdmin', 'Manager', 'HR Manager', 'Staff', 'Employee'];
    if (pathname.startsWith('/payroll')) return ['Admin', 'SuperAdmin', 'Manager', 'HR Manager'];
    if (pathname.startsWith('/performance') || pathname.startsWith('/documents') || pathname.startsWith('/hr-policies') || pathname.startsWith('/assets') || pathname.startsWith('/travel-expense') || pathname.startsWith('/announcements')) return ['Admin', 'SuperAdmin', 'Manager', 'HR Manager', 'Staff', 'Employee'];
    if (pathname.startsWith('/legal') || pathname.startsWith('/reports')) return ['Admin', 'SuperAdmin', 'Manager'];
    return null;
  };

  // Show loading state
  if (authLoading || isPageLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1f2661 0%, #2b3380 50%, #5c6bc0 100%)'
      }}>
        <div style={{
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px',
            animation: 'spin 1s linear infinite'
          }}>⏳</div>
          <p>Loading...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has required role
  const requiredRoles = allowedRoles || routeRoles(location.pathname);
  if (requiredRoles && user && !requiredRoles.includes(user.role)) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1f2661 0%, #2b3380 50%, #5c6bc0 100%)',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div>
          <h1>Access Denied</h1>
          <p>Your role ({user.role}) does not have permission to access this page.</p>
          <p>Required roles: {requiredRoles.join(', ')}</p>
        </div>
      </div>
    );
  }

  // User is authenticated and has required role
  return React.isValidElement(children)
    ? React.cloneElement(children, { user, onLogout: logout })
    : children;
};

export default ProtectedRoute;
